'use client';
import { Country } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getErrorTranslationKey, streetValidator } from '@/utils/form-rule';
import { Box } from '@mui/material';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddressFormData } from '.';
import AutoComplete from '../Form/AutoComplete';
import Input, { type FloatingLabelVariant } from '../Form/FormInput';
import useHydratedWhenEnabled from '../Form/useHydratedWhenEnabled';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';
import { useStore } from '@/providers/app-store-provider';

export default function FormItemStreet({
  formData,
  countryList,
  floatingLabelVariant,
  shrinkLabelWhenFilled = false,
  overlayZIndex,
}: {
  formData: UseFormReturn<AddressFormData, any, AddressFormData>;
  countryList: Country[];
  floatingLabelVariant?: FloatingLabelVariant;
  shrinkLabelWhenFilled?: boolean;
  overlayZIndex?: number;
}) {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const publicResourceData = useStore((state) => state.publicResourceData);
  const addressAutocompleteCountries: Record<string, { language: string }> = publicResourceData
    ?.jsonvalues?.address_auto_complete_area || {
    US: {
      language: 'en',
    },
    CA: {
      language: 'en',
    },
    GB: {
      language: 'en',
    },
    FR: {
      language: 'fr',
    },
  };

  const [addressList, setAddressList] = useState<
    Array<{
      id: number;
      description: string;
      place_id: string;
    }>
  >([]);
  const { deviceType } = useContext(ResponsiveContext);
  const [street, setStreet] = useState<string>(formData.getValues('street'));
  const [isStreetFocused, setIsStreetFocused] = useState(false);
  const isHydrated = useHydratedWhenEnabled(shrinkLabelWhenFilled);
  const renderedStreet = shrinkLabelWhenFilled && !isHydrated ? '' : street;
  formData.watch((value) => {
    setStreet(value.street);
  });
  const [service, setService] = useState<any>();
  const [placeService, setPlaceService] = useState<any>();
  const [sessionToken, setSessionToken] = useState<any>();
  // 注册谷歌地址自动填充
  const initMap = () => {
    try {
      setService(new (window as any).google.maps.places.AutocompleteService());
      setPlaceService(
        new (window as any).google.maps.places.PlacesService(document.createElement('div')),
      );
      setSessionToken(new (window as any).google.maps.places.AutocompleteSessionToken());
    } catch (error) {
      logger.error(error);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  // 请求谷歌地址自动填充
  const autoComplete = (queryString: string) => {
    if (!service) {
      initMap();
      return;
    }
    setIsLoading(true);
    service.getPlacePredictions(
      {
        input: queryString,
        sessionToken,
        language:
          addressAutocompleteCountries?.[formData.getValues('country_id')]?.language || 'en',
        componentRestrictions: { country: formData.getValues('country_id').toLowerCase() },
      },
      (predictions, status) => {
        const _addressList = [];
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
          predictions.map((item, index) => {
            const termsValueArr = item.terms.slice(0, -1).map((term) => {
              return term.value;
            });
            let minLength = 3;
            if (formData.getValues('country_id') === 'FR') {
              minLength = 2;
            }
            const hasEnoughAddressTerms =
              formData.getValues('country_id') === 'FR'
                ? termsValueArr.length >= minLength
                : termsValueArr.length > minLength;
            if (hasEnoughAddressTerms) {
              _addressList.push({
                id: index,
                description: termsValueArr.join(', '),
                place_id: item.place_id,
              });
              return {
                id: index,
                description: termsValueArr.join(', '),
                place_id: item.place_id,
              };
            } else {
              return '';
            }
          });
          if (formData.getValues('street')) {
            setAddressList(_addressList || []);
          } else {
            setAddressList([]);
          }
          setIsLoading(false);
        } else {
          setAddressList(_addressList || []);
          setIsLoading(false);
        }
      },
    );
  };
  // 谷歌自动地址填充请求触发
  const { run: querySearch } = useDebounceFn(
    (value: string) => {
      formData.setValue('street', value);
      formData.trigger('street');
      if (!value) {
        setAddressList([]);
        return;
      }
      autoComplete(value);
    },
    {
      wait: 1000,
    },
  );

  const onStreetSelect = (item: { id: number; description: string; place_id: string }) => {
    setAddressList([]);
    const streetList = item.description.split(', ');
    if (formData.getValues('country_id') === 'FR') {
      streetList.length -= 1;
    } else {
      streetList.length -= 2;
    }
    formData.setValue('street', streetList.join(', '));
    placeService.getDetails(
      {
        placeId: item.place_id,
        fields: ['address_components'],
        sessionToken,
      },
      (details) => {
        let province_types = 'administrative_area_level_1';
        if (formData.getValues('country_id') === 'FR') {
          province_types = 'administrative_area_level_2';
        }
        details.address_components.forEach((entry) => {
          if (entry.types[0] === 'postal_code') {
            formData.setValue('postcode', entry.long_name);
          } else if (entry.types[0] === 'locality') {
            formData.setValue('city', entry.long_name);
          } else if (entry.types[0] === province_types) {
            formData.setValue('province', entry.long_name);
            const temp = countryList.filter(
              (_item) => _item.country_id === formData.getValues('country_id'),
            );
            const temp2 = temp[0].province.filter((_item) => _item.name === entry.long_name);
            formData.setValue('province_id', temp2[0].region_id);
            formData.setValue('region_code', temp2[0].code);
          }
          formData.trigger(['street', 'province', 'postcode', 'city']);
        });
      },
    );
  };

  useEffect(() => {
    initMap();
  }, []);

  const isWeb = [EPlatform.WEB].includes(deviceType);
  const isInsideFloatingLabel = floatingLabelVariant === 'inside';
  const isStreetLabelCompact = isInsideFloatingLabel
    ? isStreetFocused || Boolean(street?.trim())
    : !isWeb;
  const streetLabel =
    isInsideFloatingLabel && !isWeb && !isStreetLabelCompact
      ? undefined
      : isStreetLabelCompact
        ? t('addressMobileLabel')
        : t('addressWebLabel');
  const isMobileInsideEmpty = isInsideFloatingLabel && !isWeb && !isStreetLabelCompact;

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        '&:focus-within .FormItemAddressPlaceholder': {
          display: 'none',
        },
      }}
      onFocus={() => setIsStreetFocused(true)}
      onBlur={() => setIsStreetFocused(false)}
    >
      {addressAutocompleteCountries?.[formData.getValues('country_id')] ||
      ['US', 'CA', 'GB'].includes(formData.getValues('country_id')) ? (
        <AutoComplete
          name="street"
          floatingLabelVariant={floatingLabelVariant}
          freeSolo
          shrinkLabelWhenFilled={shrinkLabelWhenFilled}
          label={streetLabel}
          control={formData.control}
          popperZIndex={overlayZIndex}
          options={addressList.map((item) => item.description)}
          filterOptions={
            /**
             * 只给 FR 加条件，是为了不改变其他站的交互
             * fr： 输入 16 sentier des vaux de rome, 94800 villejuif, France
             */
            formData.getValues('country_id') === 'FR' ? (options) => options : undefined
          }
          disableClearable
          multiple={false}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') {
              querySearch(value);
            }
          }}
          onChange={(_, value, reason) => {
            if (reason === 'selectOption') {
              onStreetSelect(addressList.find((item) => item.description === value));
            }
          }}
          optionTextIsEllipsis
          blurOnSelect
          rules={{
            validate: () => {
              const errorCode = streetValidator(formData.getValues('street'));
              if (errorCode) {
                const translationKey = getErrorTranslationKey(errorCode);
                return translationKey ? tValidation(translationKey as any) : 'Validation error';
              }
              return true;
            },
          }}
          loading={isLoading}
        />
      ) : (
        <Input
          name="street"
          floatingLabelVariant={floatingLabelVariant}
          shrinkLabelWhenFilled={shrinkLabelWhenFilled}
          label={streetLabel}
          control={formData.control}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 50,
              },
            },
          }}
          rules={{
            validate: () => {
              const errorCode = streetValidator(formData.getValues('street'));
              if (errorCode) {
                const translationKey = getErrorTranslationKey(errorCode);
                return translationKey ? tValidation(translationKey as any) : 'Validation error';
              }
              return true;
            },
          }}
        />
      )}
      <WapOnly>
        {!renderedStreet && (
          <div
            className={`FormItemAddressPlaceholder absolute top-[8px] h-[46px] f14 right-[16px] flex items-center text-auxiliary-dark-gray pointer-events-none ${isMobileInsideEmpty ? 'left-[12px]' : 'left-[96px]'}`}
          >
            {t('addressPlaceholder')}
          </div>
        )}
      </WapOnly>
    </Box>
  );
}
